import { useEffect, useState } from "react";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import { fmtDateTime } from "../lib/format";
import { Bell, Check, CheckCheck, Trash2, Info, AlertTriangle, CircleCheck, OctagonAlert, Send, Megaphone, User, Hash } from "lucide-react";
import { toast } from "sonner";

const typeMeta = {
  info: { Icon: Info, color: "text-blue-600", bg: "bg-blue-50", border: "border-blue-200" },
  success: { Icon: CircleCheck, color: "text-emerald-600", bg: "bg-emerald-50", border: "border-emerald-200" },
  warning: { Icon: AlertTriangle, color: "text-amber-600", bg: "bg-amber-50", border: "border-amber-200" },
  danger: { Icon: OctagonAlert, color: "text-red-600", bg: "bg-red-50", border: "border-red-200" },
};

export default function Notifications() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [composeOpen, setComposeOpen] = useState(false);
  const [broadcast, setBroadcast] = useState({ title: "", message: "", type: "info", audience: "all" });
  const [sending, setSending] = useState(false);

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/notifications");
    setRows(data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const markRead = async (id) => {
    await api.patch(`/notifications/${id}/read`);
    load();
  };
  const markAll = async () => {
    await api.post("/notifications/read-all");
    toast.success("All marked as read");
    load();
  };
  const remove = async (id) => {
    await api.delete(`/notifications/${id}`);
    toast.success("Deleted");
    load();
  };

  const sendBroadcast = async (e) => {
    e.preventDefault();
    if (!broadcast.title.trim() || !broadcast.message.trim()) {
      toast.error("Title and message are required");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post("/notifications/broadcast", broadcast);
      toast.success(`Broadcast sent to ${data.sent} user(s)`);
      setBroadcast({ title: "", message: "", type: "info", audience: "all" });
      setComposeOpen(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Broadcast failed");
    } finally {
      setSending(false);
    }
  };

  const unread = rows.filter((r) => !r.read).length;

  return (
    <div data-testid="notifications-page">
      <PageHeader
        title="Notifications"
        subtitle={`${unread} unread · ${rows.length} total`}
        actions={(
          <div className="flex items-center gap-2">
            <button onClick={() => setComposeOpen((v) => !v)} className="h-9 px-3 bg-emerald-500 hover:bg-emerald-600 text-white rounded-sm text-sm font-semibold inline-flex items-center gap-1.5" data-testid="notif-broadcast-btn">
              <Megaphone size={14} /> {composeOpen ? "Close" : "Broadcast to users"}
            </button>
            <button onClick={markAll} disabled={unread === 0} className="h-9 px-3 border border-gray-200 rounded-sm text-sm inline-flex items-center gap-1.5 disabled:opacity-50 hover:bg-gray-50" data-testid="notif-mark-all">
              <CheckCheck size={14} /> Mark all read
            </button>
          </div>
        )}
      />

      {composeOpen && (
        <form onSubmit={sendBroadcast} className="bg-white border border-emerald-200 rounded-sm p-5 mb-5 space-y-3" data-testid="notif-broadcast-form">
          <div className="flex items-center gap-2 text-sm font-semibold text-emerald-700">
            <Send size={14} /> Compose broadcast — delivered in-app to matching users
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-1">Title</label>
              <input value={broadcast.title} onChange={(e) => setBroadcast({ ...broadcast, title: e.target.value })} className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm" placeholder="System Maintenance Tonight" data-testid="broadcast-title" required />
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-1">Audience</label>
              <select value={broadcast.audience} onChange={(e) => setBroadcast({ ...broadcast, audience: e.target.value })} className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm" data-testid="broadcast-audience">
                <option value="all">All users</option>
                <option value="active">Active only</option>
                <option value="blocked">Blocked only</option>
                <option value="vip">VIP tier (non-Normal)</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-1">Message</label>
            <textarea value={broadcast.message} onChange={(e) => setBroadcast({ ...broadcast, message: e.target.value })} rows={3} className="w-full px-3 py-2 border border-gray-200 rounded-sm text-sm" placeholder="Dear players, we will be down 2:00–2:30 AM for upgrades." data-testid="broadcast-message" required />
          </div>
          <div className="flex items-center gap-2">
            <select value={broadcast.type} onChange={(e) => setBroadcast({ ...broadcast, type: e.target.value })} className="h-9 px-3 border border-gray-200 rounded-sm text-sm" data-testid="broadcast-type">
              <option value="info">Info</option>
              <option value="success">Success</option>
              <option value="warning">Warning</option>
              <option value="danger">Alert</option>
              <option value="bonus">Bonus</option>
              <option value="promo">Promo</option>
            </select>
            <button type="submit" disabled={sending} className="h-9 px-4 bg-emerald-500 hover:bg-emerald-600 text-white rounded-sm text-sm font-semibold inline-flex items-center gap-1.5 disabled:opacity-60" data-testid="broadcast-submit">
              <Send size={13} /> {sending ? "Sending…" : "Send broadcast"}
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="bg-white border border-gray-200 rounded-sm p-10 text-center text-sm text-gray-500">Loading...</div>
      ) : rows.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-sm p-10 text-center text-sm text-gray-500 flex flex-col items-center gap-2">
          <Bell size={28} className="text-gray-300" />
          <span>No notifications yet.</span>
        </div>
      ) : (
        <div className="space-y-2" data-testid="notif-list">
          {rows.map((n) => {
            const meta = typeMeta[n.type] || typeMeta.info;
            const isSystem = !n.user_name || n.user_name.startsWith("(");
            const handle = isSystem ? "system" : n.user_name.replace(/^@/, "");
            return (
              <div key={n.id} className={`bg-white border ${n.read ? "border-gray-200" : "border-emerald-300"} rounded-sm p-4 flex items-start gap-4 ${n.read ? "" : "shadow-[0_0_0_1px_rgba(16,185,129,0.15)]"}`} data-testid={`notif-${n.id}`}>
                <div className={`h-10 w-10 ${meta.bg} ${meta.color} border ${meta.border} rounded-sm flex items-center justify-center flex-shrink-0`}>
                  <meta.Icon size={18} />
                </div>
                <div className="flex-1 min-w-0">
                  {/* Title + unread indicator */}
                  <div className="flex items-center gap-2">
                    <div className="font-semibold text-[14px] text-gray-900 leading-snug">{n.title}</div>
                    {!n.read && <span className="h-2 w-2 rounded-full bg-emerald-500 flex-shrink-0" />}
                  </div>

                  {/* Message */}
                  <div className="text-[13px] text-gray-600 mt-1 leading-relaxed">{n.message}</div>

                  {/* Recipient row — "To: @username [USER]" pill style */}
                  <div className="mt-2.5 flex items-center gap-2 flex-wrap" data-testid={`notif-user-${n.id}`}>
                    <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-slate-50 border border-slate-200 text-[11px]">
                      <span className="text-slate-500 font-medium uppercase tracking-wider text-[10px]">To:</span>
                      <span
                        className={`font-semibold ${isSystem ? "text-slate-400 italic" : "text-slate-800"}`}
                        title={n.user_email || ""}
                      >
                        @{handle}
                      </span>
                    </span>
                    {!isSystem && (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-indigo-50 border border-indigo-200 text-indigo-700 text-[10px] font-bold tracking-wider uppercase">
                        User
                      </span>
                    )}
                    {isSystem && (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-amber-50 border border-amber-200 text-amber-700 text-[10px] font-bold tracking-wider uppercase">
                        Broadcast
                      </span>
                    )}
                    {n.player_id && (
                      <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-gray-50 border border-gray-200 text-gray-600 text-[10px] font-mono">
                        <Hash size={9} strokeWidth={2.5} />{n.player_id}
                      </span>
                    )}
                  </div>

                  {/* Timestamp */}
                  <div className="text-[11px] text-gray-400 mt-2 uppercase tracking-wider font-medium">{fmtDateTime(n.created_at)}</div>
                </div>
                <div className="flex items-center gap-1">
                  {!n.read && (
                    <button onClick={() => markRead(n.id)} className="h-8 w-8 inline-flex items-center justify-center border border-gray-200 rounded-sm text-gray-500 hover:text-emerald-600 hover:border-emerald-200" data-testid={`notif-read-${n.id}`}>
                      <Check size={14} />
                    </button>
                  )}
                  <button onClick={() => remove(n.id)} className="h-8 w-8 inline-flex items-center justify-center border border-gray-200 rounded-sm text-gray-500 hover:text-red-600 hover:border-red-200" data-testid={`notif-delete-${n.id}`}>
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
